"use client";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import CloseIcon from "@mui/icons-material/Close";
import HomeIcon from "@mui/icons-material/Home";
import LogoutIcon from "@mui/icons-material/Logout";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import PaymentIcon from "@mui/icons-material/Payment";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import RssFeedIcon from "@mui/icons-material/RssFeed";
import { useSession } from "next-auth/react";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { ADMIN_APP_TOOLS } from "~/components/admin/AdminAppTools";
import Link from "~/components/Link";
import { useUnpaidOrders } from "~/components/providers/UnpaidOrdersContext";
import { ThemeModeToggle } from "~/components/theme";
import { trackEvent } from "~/connectors/analytics.connector";
import { AnalyticsEvents } from "~/connectors/analyticsEvents";
import { authService } from "~/connectors/auth.connector";
import UserAvatar from "./UserAvatar";

import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Toolbar from "@mui/material/Toolbar";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

interface UserMenuProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
}

export default function UserMenu({ anchorEl, onClose }: UserMenuProps) {
  const router = useRouter();
  const { data: session } = useSession();
  const pathname = usePathname();
  const { unpaidOrders, hasUnpaidOrders } = useUnpaidOrders();
  const [adminMenuAnchorEl, setAdminMenuAnchorEl] =
    useState<null | HTMLElement>(null);

  const user = session?.user;
  const showBackToWebsite = pathname.startsWith("/admin");
  const showAdminEntry =
    user?.role === "admin" && !pathname.startsWith("/admin");

  // Track when user menu is opened
  useEffect(() => {
    if (anchorEl) {
      trackEvent(AnalyticsEvents.USER_MENU_OPENED);
    }
  }, [anchorEl]);

  const handleMenuItemClick = (href: string, event: string) => {
    trackEvent(event);
    router.push(href);
    onClose();
  };

  const handleLogout = async () => {
    trackEvent(AnalyticsEvents.USER_MENU_CLICKED_DECONECTARE);
    await authService.logout("/");
    onClose();
  };

  const handleBackToWebsite = () => {
    trackEvent(AnalyticsEvents.USER_MENU_CLICKED_INAPOI);
    router.push("/");
    onClose();
  };

  const handleAdminArrowOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAdminMenuAnchorEl(event.currentTarget);
  };

  const handleAdminMenuClose = () => {
    setAdminMenuAnchorEl(null);
  };

  const handleAdminToolClick = (href: string) => {
    handleAdminMenuClose();
    router.push(href);
    onClose();
  };

  if (!user) return null;

  const firstName = user.name ? user.name.split(" ")[0] : null;
  const greeting = firstName ? `Salut, ${firstName}` : "Salut";

  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      slotProps={{
        paper: {
          sx: {
            mt: 1.5,
            borderRadius: 2,
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
            minWidth: 280,
            maxWidth: 320,
          },
        },
      }}
    >
      {/* Toolbar with email and close button */}
      <Toolbar
        sx={{
          minHeight: 56,
          px: 2,
          borderBottom: `1px solid var(--mui-palette-divider)`,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            flexGrow: 1,
            fontSize: "0.875rem",
          }}
        >
          {user.email}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Toolbar>

      {/* User info section — clicking avatar navigates to Contul Meu */}
      <Box
        sx={{
          p: 3,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Tooltip title="Contul Meu">
          <IconButton
            onClick={() =>
              handleMenuItemClick(
                "/dashboard/profil",
                AnalyticsEvents.USER_MENU_CLICKED_PROFIL,
              )
            }
            sx={{ p: 0 }}
          >
            <UserAvatar user={user} size={64} />
          </IconButton>
        </Tooltip>
        <Typography
          variant="h6"
          sx={{
            mt: 2,
            fontWeight: 500,
          }}
        >
          {greeting}
        </Typography>
      </Box>

      <Divider />

      {/* Resume payment - show if there are unpaid orders */}
      {hasUnpaidOrders && (
        <MenuItem
          onClick={() => {
            if (unpaidOrders?.data.length === 1) {
              handleMenuItemClick(
                `/dashboard/comenzi/${unpaidOrders.data[0].id}`,
                AnalyticsEvents.USER_MENU_CLICKED_REIA_PLATA,
              );
            } else {
              handleMenuItemClick(
                "/dashboard/comenzi",
                AnalyticsEvents.USER_MENU_CLICKED_REIA_PLATA,
              );
            }
          }}
          sx={{
            py: 1.5,
            position: "relative",
            // draw a thin warning stripe on the right spanning full item height
            "&::after": {
              content: '""',
              position: "absolute",
              right: 0,
              top: 0,
              bottom: 0,
              width: 8,
              bgcolor: "warning.main",
              borderRadius: 0,
              pointerEvents: "none",
            },
            "&:hover": {
              bgcolor: "action.hover",
            },
          }}
        >
          <PaymentIcon sx={{ mr: 2 }} />
          Reia plata
        </MenuItem>
      )}
      {hasUnpaidOrders && <Divider />}

      {/* Menu items */}
      <MenuItem
        onClick={() =>
          handleMenuItemClick(
            "/dashboard/cantec",
            AnalyticsEvents.USER_MENU_CLICKED_CANTECELE,
          )
        }
        sx={{ py: 1.5 }}
      >
        <MusicNoteIcon sx={{ mr: 2, color: "inherit" }} />
        Cântecelele Mele
      </MenuItem>

      <MenuItem
        onClick={() =>
          handleMenuItemClick(
            "/dashboard/comenzi",
            AnalyticsEvents.USER_MENU_CLICKED_COMENZI,
          )
        }
        sx={{ py: 1.5 }}
      >
        <ReceiptLongIcon sx={{ mr: 2, color: "inherit" }} />
        Comenzile Mele
      </MenuItem>

      <MenuItem
        onClick={() =>
          handleMenuItemClick(
            "/dashboard/profil",
            AnalyticsEvents.USER_MENU_CLICKED_PROFIL,
          )
        }
        sx={{ py: 1.5 }}
      >
        <AccountCircleIcon sx={{ mr: 2, color: "inherit" }} />
        Contul Meu
      </MenuItem>

      {/* Admin menu item with expand arrow for admin tools submenu */}
      {showAdminEntry && <Divider sx={{ my: 1 }} />}
      {showAdminEntry && (
        <Box sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}>
          <MenuItem
            sx={{ py: 1.5, pr: 1, flexGrow: 1, }}
          >
            <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1 }} onClick={() =>
              handleMenuItemClick(
                "/admin",
                AnalyticsEvents.USER_MENU_CLICKED_ADMIN,
              )
            }>
              <AdminPanelSettingsIcon
                sx={{ mr: 2, color: "var(--mui-palette-error-main)" }}
              />
              <Box sx={{ flexGrow: 1 }}>Admin</Box>
            </Box>
          </MenuItem>
          <Tooltip title="Instrumente admin">
            <IconButton
              size="small"
              onClick={handleAdminArrowOpen}
              sx={{
                ml: 1,
                color: "text.secondary",
                "&:hover": { color: "error.main" },
              }}
            >
              <ArrowRightIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* Admin tools submenu */}
      <Menu
        anchorEl={adminMenuAnchorEl}
        open={Boolean(adminMenuAnchorEl)}
        onClose={handleAdminMenuClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: 2,
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
              minWidth: 250,
              overflowY: "auto",
            },
          },
        }}
      >
        {ADMIN_APP_TOOLS.map((tool) => {
          const IconComponent = tool.icon;
          return (
            <MenuItem
              key={tool.id}
              onClick={() => handleAdminToolClick(tool.href)}
              sx={{ py: 1 }}
            >
              <ListItemIcon
                sx={{
                  "> img": {
                    width: 20,
                    height: 20,
                  },
                }}
              >
                <IconComponent fontSize="small" />
              </ListItemIcon>
              {tool.title}
            </MenuItem>
          );
        })}
      </Menu>

      {/* Back to website + navigation links (shown when in admin) */}
      {showBackToWebsite && <Divider sx={{ my: 1 }} />}
      {showBackToWebsite && (
        <MenuItem onClick={handleBackToWebsite} sx={{ py: 1.5 }}>
          <HomeIcon sx={{ mr: 2, color: "inherit" }} />
          Website
        </MenuItem>
      )}
      {showBackToWebsite && (
        <MenuItem
          onClick={() => {
            router.push("/versuri");
            onClose();
          }}
          sx={{ py: 1.5 }}
        >
          <AutoStoriesIcon sx={{ mr: 2, color: "inherit" }} />
          Versuri
        </MenuItem>
      )}
      {showBackToWebsite && (
        <MenuItem
          onClick={() => {
            router.push("/blog");
            onClose();
          }}
          sx={{ py: 1.5 }}
        >
          <RssFeedIcon sx={{ mr: 2, color: "inherit" }} />
          Blog
        </MenuItem>
      )}

      <Divider sx={{ my: 1 }} />

      {/* Logout */}
      <MenuItem onClick={handleLogout} sx={{ py: 1.5 }}>
        <LogoutIcon sx={{ mr: 2 }} />
        Deconectare
      </MenuItem>

      <Divider sx={{ my: 1 }} />

      {/* Bottom section with theme toggle, terms and confidentiality */}
      <Box sx={{ px: 2, py: 1.5, textAlign: "center" }}>
        {/* Theme toggle */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mb: 1.5,
          }}
        >
          <ThemeModeToggle />
        </Box>

        {/* Terms and confidentiality links */}
        <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
          <Link
            href="/termeni"
            sx={{
              fontSize: "0.75rem",
              color: "var(--mui-palette-text-secondary)",
              textDecoration: "none",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            Termeni și condiții
          </Link>
          <Link
            href="/confidentialitate"
            sx={{
              fontSize: "0.75rem",
              color: "var(--mui-palette-text-secondary)",
              textDecoration: "none",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            Confidențialitate
          </Link>
        </Box>
      </Box>
    </Menu>
  );
}
